/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================
* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)
Coded by www.creative-tim.com
 =========================================================
* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useContext, useEffect } from 'react'

// @mui core components
import {
  Box,
  Card,
  Grid,
  Typography,
  Button,
  Tooltip,
  IconButton,
  Fade,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import HelpIcon from '@mui/icons-material/Help'

// Settings page components
import FormField from 'components/FormField'

// Data
import { IntakeContext } from 'context/IntakeContext'
import { selectData } from 'layouts/onboarding'

// Popup
import { toast } from 'react-toastify'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'

import { useState } from 'react'

// Debounce
import { useFormDebounce } from 'hooks/useFormDebounce'
import styled, { keyframes } from 'styled-components'
import LoopIcon from '@mui/icons-material/Loop'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import OnboardingMultiSelectTile from 'components/OnboardingMultiSelectTile'

import SoftTypography from 'components/SoftTypography'
import OnboardingMultiOptionTile from 'components/OnboardingMultiOptionTile'
import { format, subYears, differenceInYears } from 'date-fns'

const rotatingIcon = keyframes`
  0% {
    transform: rotate(360deg);
  }
  100% {
    transform: rotate(0deg);
  }
`
const colorChangeIcon = keyframes`
  0% {
    color: #66d432;
  }
  50% {
    color: #66d432;
  }
  100% {
    color: #344767;
  }
`
const ChangesSavedIcon = styled(CheckCircleOutlineIcon)`
  &.saved {
    color: #344767;
    font-size: 1.5rem !important;
    animation: 2s ${colorChangeIcon} ease-out;
  }
`
const SavingIcon = styled(LoopIcon)`
  &.loading {
    color: #344767;
    font-size: 1.5rem !important;
    animation: 1.5s ${rotatingIcon} linear infinite;
  }
`

function Family() {
  const { intakeState, updateIntakeStateAtKey, updateUser } =
    useContext(IntakeContext)

  const { updateIntakeStateAndSubmit, loading } = useFormDebounce()

  const [ageQuestion, setAgeQuestion] = useState(true)

  const dateToYears = intakeState.familySpouseDateOfBirth
    ? differenceInYears(
        new Date(),
        new Date(intakeState.familySpouseDateOfBirth)
      )
    : ''
  const yearsToDate = (years) =>
    format(subYears(new Date(), years), 'yyyy-MM-dd')

  // add an onSubmit button and call the context function `updateUser`
  // const onSubmit = () => updateUser()
  const onSubmit = async () => {
    await updateUser()
    toast.info('You have successfully updated your family details', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }
  const maritalStatus = intakeState?.familyMaritalStatus
  const divorcedStatus = intakeState?.familyDivorced
  const divorcedOwed = intakeState?.familyDivorcedOwedPension

  useEffect(() => {
    // console.log(intakeState)
  }, [intakeState])

  return (
    <Card
      sx={{
        textAlign: 'left',
        boxShadow: 0,
        border: '1px solid #ddd',
        borderRadius: 2,
      }}
    >
      <Box px={3} pt={3} display="flex" justifyContent="space-between">
        <Typography variant="h3" fontSize={{ xs: '24px', md: '30px' }}>
          Family
        </Typography>

        <Button
          variant="gradient"
          color="dark"
          sx={{ height: '100%' }}
          onClick={onSubmit}
          startIcon={
            loading ? (
              <>
                <SavingIcon fontSize="medium" className={'loading'} />
              </>
            ) : (
              <>
                <ChangesSavedIcon fontSize="medium" className={'saved'} />
              </>
            )
          }
        >
          {loading ? `updating...` : 'up to date'}
        </Button>
      </Box>
      <Box component="form" pb={3} px={3}>
        <Grid container columnSpacing={3} rowSpacing={2}>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'familyMaritalStatus',
              label: 'What is your current Marital Status?',
              placeholder: intakeState.familyMaritalStatus,
              inputType: 'select',
              value: intakeState.familyMaritalStatus,
              onChange: updateIntakeStateAndSubmit((familyMaritalStatus) =>
                updateIntakeStateAtKey({
                  familyMaritalStatus: familyMaritalStatus,
                  familyDivorced:
                    familyMaritalStatus === 'divorced' ? true : false,
                })
              ),
              options: selectData.relationshipStatus,
            })}
          </Grid>

          {maritalStatus === 'married' && (
            <>
              <Grid item xs={0} sm={6}></Grid>
              <Grid item xs={12} sm={6}>
                {renderMuiFormField({
                  id: 'familySpouseName',
                  label: "Spouse's Name",
                  placeholder: "Spouse's Name",
                  inputType: 'text',
                  //   defaultErrorState: currentErrors[intakeState.firstName],
                  value: intakeState.familySpouseName,
                  onChange: updateIntakeStateAndSubmit((familySpouseName) =>
                    updateIntakeStateAtKey({
                      familySpouseName,
                    })
                  ),
                })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {renderMuiFormField({
                  id: 'familySpousePhone',
                  label: "Spouse's Phone #",
                  placeholder: "Spouse's #",
                  inputType: 'tel',
                  //   defaultErrorState: currentErrors[intakeState.firstName],
                  value: intakeState.familySpousePhone,
                  onChange: updateIntakeStateAndSubmit((familySpousePhone) =>
                    updateIntakeStateAtKey({
                      familySpousePhone,
                    })
                  ),
                })}
              </Grid>

              {ageQuestion ? (
                <>
                  <Grid item xs={12} sm={6}>
                    <Grid item xs={12}>
                      {renderMuiFormField({
                        id: 'familySpouseDateOfBirth',
                        label: 'Spouse Date of Birth',
                        inputType: 'date',
                        defaultDate: '1970-01-01',
                        dateListTrim: { start: 0, end: 80 },
                        value: intakeState.familySpouseDateOfBirth,
                        onChange: updateIntakeStateAndSubmit(
                          (familySpouseDateOfBirth) =>
                            updateIntakeStateAtKey({
                              familySpouseDateOfBirth,
                            })
                        ),
                      })}
                    </Grid>

                    <Box display="flex" justifyContent="flex-start">
                      <Button
                        variant="text"
                        color="info"
                        sx={{ height: '100%' }}
                        onClick={() => setAgeQuestion(false)}
                      >
                        Use their age instead
                      </Button>
                    </Box>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} sm={6}>
                    <Grid item xs={12}>
                      <FormField
                        label={<>Spouse&apos;s Age</>}
                        placeholder="e.g. 62"
                        value={dateToYears}
                        onChange={(e) => {
                          updateIntakeStateAtKey({
                            familySpouseDateOfBirth: yearsToDate(
                              e.target.value
                            ),
                          })
                        }}
                      />
                    </Grid>

                    <Box display="flex" justifyContent="flex-start">
                      <Button
                        variant="text"
                        color="info"
                        sx={{ height: '100%' }}
                        onClick={() => setAgeQuestion(true)}
                      >
                        Use their date of birth instead
                      </Button>
                    </Box>
                  </Grid>
                </>
              )}
            </>
          )}

          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'familyDivorced',
              label: 'Have you ever been divorced?',
              placeholder: intakeState.familyDivorced,
              inputType: 'select',
              value: intakeState.familyDivorced,
              onChange: updateIntakeStateAndSubmit((familyDivorced) =>
                updateIntakeStateAtKey({ familyDivorced })
              ),
              options: selectData.yesNo,
            })}
          </Grid>

          {divorcedStatus && (
            <>
              <Grid item xs={12} sm={6}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'familyDivorcedOwedPension',
                  label: 'Will your former spouse be owed any of your pension?',
                  placeholder: intakeState.familyDivorcedOwedPension,
                  inputType: 'select',
                  value: intakeState.familyDivorcedOwedPension,
                  onChange: updateIntakeStateAndSubmit(
                    (familyDivorcedOwedPension) =>
                      updateIntakeStateAtKey({ familyDivorcedOwedPension })
                  ),
                  options: selectData.yesNo,
                })}
              </Grid>

              {divorcedOwed && (
                <Grid item xs={12} sm={6}>
                  <Grid container columnSpacing={3} rowSpacing={2} pt={1}>
                    <Grid item xs={12} sm={6}>
                      {renderMuiFormField({
                        id: 'familyDivorcedMarriageStartDate',
                        label: 'Marriage date with former spouse',
                        inputType: 'date',
                        defaultDate: '1970-01-01',
                        dateListTrim: { start: 0, end: 80 },
                        intakeAttribute:
                          intakeState.familyDivorcedMarriageStartDate,
                        onChange: updateIntakeStateAndSubmit(
                          (familyDivorcedMarriageStartDate) =>
                            updateIntakeStateAtKey({
                              familyDivorcedMarriageStartDate,
                            })
                        ),
                      })}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {renderMuiFormField({
                        id: 'familyDivorcedMarriageEndDate',
                        label: 'Divorce date with former spouse',
                        inputType: 'date',
                        defaultDate: '1970-01-01',
                        dateListTrim: { start: 0, end: 80 },
                        intakeAttribute:
                          intakeState.familyDivorcedMarriageEndDate,
                        onChange: updateIntakeStateAndSubmit(
                          (familyDivorcedMarriageEndDate) =>
                            updateIntakeStateAtKey({
                              familyDivorcedMarriageEndDate,
                            })
                        ),
                      })}
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </>
          )}
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'familyChildren',
              label: 'Number of children (if any)',
              placeholder: intakeState.familyChildren,
              inputType: 'int',
              value: intakeState.familyChildren,
              onChange: updateIntakeStateAndSubmit((familyChildren) =>
                updateIntakeStateAtKey({ familyChildren })
              ),
            })}
          </Grid>
        </Grid>
      </Box>
    </Card>
  )
}

export default Family
